import React, { useCallback, useEffect, useState } from 'react';
import adminApi from '../api/client';
import CanonicalResolution, { type Resolution } from '../components/CanonicalResolution';

type Status = 'pending' | 'approved' | 'rejected';

interface Item {
  correction_id: string;
  book_id: string;
  book_title: string | null;
  field_name: string;
  confirm_count: number;
  submitted_by: string;
  submitted_by_name: string | null;
  status: Status;
  created_at: string | null;
  reviewed_at: string | null;
}

interface Detail {
  canonical_entity_linking?: boolean;
  author_ref?: Resolution['author_ref'];
  publisher_ref?: Resolution['publisher_ref'];
  correction_id: string;
  book_id: string;
  field_name: string;
  old_value: string | null;
  new_value: string;
  reason: string | null;
  confirm_count: number;
  status: Status;
  submitted_by_name: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  current_book: Record<string, any> | null;
}

const formatDate = (value: string | null) => value ? new Date(value).toLocaleString() : '—';

const SinhalaCorrections: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<Status | ''>('pending');
  const [items, setItems] = useState<Item[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [resolution, setResolution] = useState<Resolution>({});
  const [resolutionValid, setResolutionValid] = useState(true);
  const perPage = 20;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, any> = { page, per_page: perPage };
      if (statusFilter) params.status_filter = statusFilter;
      const res = await adminApi.get('/admin/sinhala-corrections', { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load Sinhala corrections.');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const openDetail = async (id: string) => {
    if (selectedId === id) {
      setSelectedId(null);
      setDetail(null);
      return;
    }
    setSelectedId(id);
    setResolution({});
    setDetail(null);
    try {
      const res = await adminApi.get<Detail>(`/admin/sinhala-corrections/${id}`);
      setDetail(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load correction detail.');
    }
  };

  const approve = async () => {
    if (!detail || detail.status !== 'pending') return;
    setActionLoading(true);
    try {
      await adminApi.post(`/admin/sinhala-corrections/${detail.correction_id}/approve`, resolution);
      setSelectedId(null);
      setDetail(null);
      await fetchQueue();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to approve Sinhala correction.');
    } finally {
      setActionLoading(false);
    }
  };

  const reject = async () => {
    if (!detail || detail.status !== 'pending' || rejectReason.trim().length < 3) return;
    setActionLoading(true);
    try {
      await adminApi.post(`/admin/sinhala-corrections/${detail.correction_id}/reject`, { reason: rejectReason.trim() });
      setSelectedId(null);
      setDetail(null);
      setRejectReason('');
      await fetchQueue();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reject Sinhala correction.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Sinhala Book Corrections</h1>
        <p className="text-sm text-gray-500 mt-0.5">Review SinhalaBook proposals before they change the catalogue.</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {(['pending', 'approved', 'rejected', ''] as const).map((status) => (
          <button
            key={status || 'all'}
            onClick={() => { setStatusFilter(status); setPage(1); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${statusFilter === status ? 'bg-primary text-white shadow' : 'bg-white text-gray-600 border border-gray-200'}`}
          >
            {status ? status[0].toUpperCase() + status.slice(1) : 'All'}
          </button>
        ))}
        <button onClick={fetchQueue} className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-600">Refresh</button>
      </div>

      {detail && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-800">Correction detail</h2>
            <span className="text-xs font-mono text-gray-500">{detail.correction_id}</span>
          </div>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div><p className="text-gray-400">Field</p><p className="font-semibold">{detail.field_name}</p></div>
            <div><p className="text-gray-400">Community confirmations</p><p className="font-semibold">{detail.confirm_count}</p></div>
            <div><p className="text-gray-400">Current value</p><p className="font-semibold break-words">{detail.current_book?.[detail.field_name] ?? detail.old_value ?? '—'}</p></div>
            <div><p className="text-gray-400">Proposed value</p><p className="font-semibold break-words">{detail.new_value}</p></div>
          </div>
          {detail.current_book && (
            <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 text-xs text-gray-600">
              Current SinhalaBook: <span className="font-semibold">{detail.current_book.title || detail.book_id}</span>
            </div>
          )}
          {detail.reason && <p className="text-sm text-gray-600">Submitter reason: {detail.reason}</p>}
          {detail.status === 'pending' && (
            <div className="flex flex-col gap-3 pt-2">
              {detail.canonical_entity_linking && <CanonicalResolution key={detail.correction_id}
                changes={{ [detail.field_name]: detail.new_value, ...(detail.author_ref ? { author_ref: detail.author_ref } : {}), ...(detail.publisher_ref ? { publisher_ref: detail.publisher_ref } : {}) }}
                isTranslation={detail.field_name === 'is_translation' ? ['true', '1', 'yes'].includes(detail.new_value.toLowerCase()) : !!detail.current_book?.is_translation} onChange={setResolution} onValidityChange={setResolutionValid} />}
              <div className="flex gap-2">
                <button disabled={actionLoading || (detail.canonical_entity_linking && !resolutionValid)} onClick={approve} className="px-4 py-2 rounded-lg bg-accent text-white font-semibold disabled:opacity-50">Approve &amp; Apply</button>
                <button disabled={actionLoading || rejectReason.trim().length < 3} onClick={reject} className="px-4 py-2 rounded-lg bg-coral text-white font-semibold disabled:opacity-50">Reject</button>
              </div>
              <input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Rejection reason (minimum 3 characters)" className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {error && <div className="px-5 py-3 text-sm text-coral bg-red-50">{error}</div>}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-gray-100 bg-gray-50/50">{['Correction', 'Book', 'Field', 'Confirmations', 'Submitted', 'Status'].map((h) => <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400">Loading…</td></tr> : items.length === 0 ? <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400">No Sinhala corrections found.</td></tr> : items.map((item) => (
                <tr key={item.correction_id} onClick={() => openDetail(item.correction_id)} className={`cursor-pointer hover:bg-gray-50 ${selectedId === item.correction_id ? 'bg-accent/5' : ''}`}>
                  <td className="px-4 py-3 text-xs font-mono text-gray-500">{item.correction_id.slice(0, 8)}…</td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-800">{item.book_title || item.book_id}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.field_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.confirm_count}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{formatDate(item.created_at)}</td>
                  <td className="px-4 py-3 text-sm font-semibold">{item.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && totalPages > 1 && <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 text-sm text-gray-500"><span>{(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total}</span><div className="flex gap-2"><button disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-3 py-1 border rounded disabled:opacity-40">Prev</button><button disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="px-3 py-1 border rounded disabled:opacity-40">Next</button></div></div>}
      </div>
    </div>
  );
};

export default SinhalaCorrections;
